const LIGHTBOX_SELECTOR =
  ".js-media-gallery a.glightbox.media-gallery__lightbox:not(.media-gallery__lightbox--filtered-out):not(.media-gallery__lightbox--more-hidden)";

const MORE_HIDDEN_CLASS = "media-gallery__lightbox--more-hidden";
const FILTERED_OUT_CLASS = "media-gallery__lightbox--filtered-out";
const MOBILE_LAYOUT_MQ = "(max-width: 768px)";

const LAYOUT_OUTER = "media-gallery__item--layout-outer";
const LAYOUT_CENTER = "media-gallery__item--layout-center";
const LAYOUT_THIRD = "media-gallery__item--layout-third";
const LAYOUT_TWO_THIRDS = "media-gallery__item--layout-two-thirds";

const ALL_LAYOUT_CLASSES = [
  LAYOUT_OUTER,
  LAYOUT_CENTER,
  LAYOUT_THIRD,
  LAYOUT_TWO_THIRDS,
];

const LEGACY_LAYOUT = [
  "media-gallery__item--third",
  "media-gallery__item--half",
  "media-gallery__item--span-one",
  "media-gallery__item--span-two",
];

let mediaGalleryLightbox = null;
/** First screenful per active tab (filter); rest load via View More. */
const GALLERY_INITIAL_VISIBLE = 10;
const GALLERY_BATCH_SIZE = 10;

function parseTags(value) {
  return (value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
}

function scrollMediaGallerySectionIntoView(root) {
  if (!root || typeof root.scrollIntoView !== "function") {
    return;
  }
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  requestAnimationFrame(() => {
    root.scrollIntoView({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "start",
    });
  });
}

/**
 * 14-column grid: three-up rows are 5+4+5 (slightly wider center than 6+3+6 on 15 cols).
 * Pair rows: 5+9 and 9+5. Cycle length 10 visible items.
 */
function syncGalleryDenseMode(root, filter) {
  const grid = root.querySelector(".media-gallery__grid");
  if (!grid) {
    return;
  }
  const key = filter || "all";
  grid.classList.toggle("media-gallery__grid--dense", key !== "all");
}

function getActiveMediaFilter(root) {
  const tab = root.querySelector(
    ".media-gallery__tab.is-active[data-media-filter]"
  );
  return tab?.dataset.mediaFilter ?? "all";
}

/**
 * Mobile: within each 10-item cycle, swap order so tiles at cycle positions 8 (video)
 * and 9 (⅓) render as 7–9 side-by-side then 8 full-width.
 */
function applyMobileGridOrder(visibleTiles) {
  const mq =
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(MOBILE_LAYOUT_MQ)
      : null;

  if (!mq || !mq.matches) {
    visibleTiles.forEach((el) => {
      el.style.removeProperty("order");
    });
    return;
  }

  visibleTiles.forEach((el, i) => {
    const pos = i % 10;
    let order = i;
    if (pos === 8) {
      order = i + 1;
    } else if (pos === 9) {
      order = i - 1;
    }
    el.style.order = String(order);
  });
}

function syncGalleryLayout(root) {
  const grid = root.querySelector(".media-gallery__grid");
  if (!grid) {
    return;
  }

  const tiles = grid.querySelectorAll(":scope > .media-gallery__lightbox");
  tiles.forEach((el) => {
    el.classList.remove(...ALL_LAYOUT_CLASSES, ...LEGACY_LAYOUT);
    el.removeAttribute("data-mg-visible-index");
    el.removeAttribute("data-mg-cycle-pos");
    el.style.removeProperty("order");
  });

  const visible = grid.querySelectorAll(
    `:scope > .media-gallery__lightbox:not(.media-gallery__lightbox--filtered-out):not(.${MORE_HIDDEN_CLASS})`
  );

  const visibleList = Array.from(visible);

  visibleList.forEach((el, i) => {
    el.setAttribute("data-mg-cycle-pos", String(i % 10));
    const r = i % 10;
    let layout = LAYOUT_OUTER;
    if (r === 1 || r === 6) {
      layout = LAYOUT_CENTER;
    } else if (r === 3 || r === 9) {
      layout = LAYOUT_THIRD;
    } else if (r === 4 || r === 8) {
      layout = LAYOUT_TWO_THIRDS;
    }
    el.classList.add(layout);
  });

  applyMobileGridOrder(visibleList);
}

function syncGalleryPaginationForFilter(root) {
  const grid = root.querySelector(".media-gallery__grid");
  if (!grid) {
    return;
  }
  const tiles = Array.from(
    grid.querySelectorAll(":scope > .media-gallery__lightbox")
  );

  tiles.forEach((el) => {
    if (el.classList.contains(FILTERED_OUT_CLASS)) {
      el.classList.remove(MORE_HIDDEN_CLASS);
    }
  });

  const visibleForFilter = tiles.filter(
    (el) => !el.classList.contains(FILTERED_OUT_CLASS)
  );

  visibleForFilter.forEach((el, i) => {
    el.classList.toggle(MORE_HIDDEN_CLASS, i >= GALLERY_INITIAL_VISIBLE);
  });
}

function updateViewMoreButton(root) {
  const wrap = root.querySelector(".media-gallery__more");
  const btn = root.querySelector(".js-media-gallery-more");
  const hasHidden = root.querySelector(
    `:scope .media-gallery__lightbox:not(.${FILTERED_OUT_CLASS}).${MORE_HIDDEN_CLASS}`
  );
  const show = Boolean(hasHidden);

  if (btn) {
    btn.hidden = !show;
    btn.setAttribute("aria-expanded", show ? "false" : "true");
  }
  if (wrap) {
    wrap.hidden = !show;
  }
}

function revealNextGalleryBatch(root) {
  const grid = root.querySelector(".media-gallery__grid");
  if (!grid) {
    return;
  }
  const hidden = Array.from(
    grid.querySelectorAll(
      `:scope > .media-gallery__lightbox:not(.${FILTERED_OUT_CLASS}).${MORE_HIDDEN_CLASS}`
    )
  );
  const toReveal = Math.min(GALLERY_BATCH_SIZE, hidden.length);
  for (let i = 0; i < toReveal; i++) {
    hidden[i].classList.remove(MORE_HIDDEN_CLASS);
  }
  syncGalleryLayout(root);
  updateViewMoreButton(root);

  if (
    mediaGalleryLightbox &&
    typeof mediaGalleryLightbox.reload === "function"
  ) {
    mediaGalleryLightbox.reload();
  }

  scrollMediaGallerySectionIntoView(root);
}

function applyMediaFilter(root, filter) {
  const key = filter || "all";
  root.querySelectorAll(".media-gallery__lightbox[data-media-tags]").forEach((link) => {
    const tags = parseTags(link.getAttribute("data-media-tags"));
    const show = key === "all" || tags.includes(key);
    link.classList.toggle(FILTERED_OUT_CLASS, !show);
  });

  syncGalleryPaginationForFilter(root);
  syncGalleryLayout(root);
  syncGalleryDenseMode(root, key);
  updateViewMoreButton(root);

  if (
    mediaGalleryLightbox &&
    typeof mediaGalleryLightbox.reload === "function"
  ) {
    mediaGalleryLightbox.reload();
  }
}

function activateTab(root, panel, tabs, activeTab) {
  tabs.forEach((tab) => {
    const on = tab === activeTab;
    tab.classList.toggle("is-active", on);
    tab.setAttribute("aria-selected", on ? "true" : "false");
  });

  if (panel && activeTab.id) {
    panel.setAttribute("aria-labelledby", activeTab.id);
  }

  applyMediaFilter(root, activeTab.dataset.mediaFilter);
  scrollMediaGallerySectionIntoView(root);
}

export function initMediaGallery() {
  const root = document.querySelector(".js-media-gallery");
  if (!root) {
    return;
  }

  const panel = root.querySelector("#media-gallery-panel");
  const tabs = Array.from(
    root.querySelectorAll(".media-gallery__tab[data-media-filter]")
  );

  if (tabs.length === 0) {
    return;
  }

  const initialFilter =
    tabs.find((tab) => tab.classList.contains("is-active"))?.dataset
      .mediaFilter ?? "all";
  applyMediaFilter(root, initialFilter);

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      activateTab(root, panel, tabs, tab);
    });
  });

  const moreBtn = root.querySelector(".js-media-gallery-more");
  if (moreBtn) {
    moreBtn.addEventListener("click", () => {
      revealNextGalleryBatch(root);
    });
  }

  const mqMobile =
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia(MOBILE_LAYOUT_MQ)
      : null;
  if (mqMobile && typeof mqMobile.addEventListener === "function") {
    mqMobile.addEventListener("change", () => {
      syncGalleryLayout(root);
    });
  } else if (mqMobile && typeof mqMobile.addListener === "function") {
    mqMobile.addListener(() => {
      syncGalleryLayout(root);
    });
  }

  const hasLinks = root.querySelector(".media-gallery__lightbox");
  if (typeof GLightbox !== "undefined" && hasLinks) {
    mediaGalleryLightbox = GLightbox({
      selector: LIGHTBOX_SELECTOR,
      loop: true,
    });
  }
}